import { forwardRef, useMemo } from 'react'
import * as THREE from 'three'
import type { ThreeElements } from '@react-three/fiber'
import { TONES, type Tone, type ToneSpec } from './theme'
import { getSurfaceMaterial } from './materials'
import { useRenderStyle } from './style'
import {
  boxGeo,
  capsuleGeo,
  cylinderGeo,
  ellipseSlabGeo,
  latheGeo,
  leafGeo,
  panelGeo,
  roundedBoxGeo,
  slabGeo,
  sphereGeo,
  stadiumSlabGeo,
  torusGeo,
} from './geometry'

type MeshProps = Omit<ThreeElements['mesh'], 'material' | 'geometry' | 'ref' | 'args'>

export interface PartProps extends MeshProps {
  geometry: THREE.BufferGeometry
  /** Semantic surface. Prefer this over `color` so themes stay swappable. */
  tone?: Tone
  /** Escape hatch for per-instance colours (accent overrides, plant variants). */
  color?: string
  opacity?: number
  /** Set false to keep this part out of the merged contour pass. */
  outline?: boolean
  /** Per-part dihedral threshold override, in degrees. */
  outlineThreshold?: number
}

/**
 * The single building block every asset is made of: a mesh with a shared,
 * tone-derived surface material, tagged so the enclosing `Outlined` wrapper can
 * fold its silhouette into one merged contour draw call.
 *
 * Never apply a non-uniform `scale` to a Part — the shading shader assumes an
 * orthonormal model matrix. Pass dimensions to the geometry factory instead.
 */
export const Part = forwardRef<THREE.Mesh, PartProps>(function Part(
  { geometry, tone = 'shell', color, opacity, outline = true, outlineThreshold, ...rest },
  ref,
) {
  const style = useRenderStyle()

  const material = useMemo(() => {
    const spec = TONES[tone] as ToneSpec
    return getSurfaceMaterial({
      color: color ?? style.palette[spec.color],
      opacity: opacity ?? spec.opacity ?? 1,
      transparent: spec.transparent ?? (opacity !== undefined && opacity < 1),
      depthWrite: spec.depthWrite ?? true,
      side: spec.side ?? THREE.FrontSide,
    })
  }, [tone, color, opacity, style])

  const userData = useMemo(
    () => ({ outline, outlineThreshold }),
    [outline, outlineThreshold],
  )

  return <mesh ref={ref} geometry={geometry} material={material} userData={userData} {...rest} />
})

/* ------------------------------------------------------------ ergonomics */

type Base = Omit<PartProps, 'geometry'>

export interface BoxPartProps extends Base {
  /** `[width (x), height (y), depth (z)]` in metres. */
  size: [number, number, number]
}

export function Box({ size, ...rest }: BoxPartProps) {
  return <Part geometry={boxGeo(size[0], size[1], size[2])} {...rest} />
}

export interface RBoxPartProps extends BoxPartProps {
  radius?: number
  segments?: number
}

/** Box bevelled on every edge. Small radii only — big ones dissolve the contour. */
export function RBox({ size, radius = 0.012, segments = 2, ...rest }: RBoxPartProps) {
  return <Part geometry={roundedBoxGeo(size[0], size[1], size[2], radius, segments)} {...rest} />
}

export interface SlabPartProps extends Base {
  /** `[width (x), depth (z), height (y)]`. */
  size: [number, number, number]
  radius?: number
}

/** Plan-rounded slab: crisp top and bottom, rounded corners in plan view. */
export function Slab({ size, radius = 0.02, ...rest }: SlabPartProps) {
  return <Part geometry={slabGeo(size[0], size[1], size[2], radius)} {...rest} />
}

/** Racetrack plan — boardroom tables, oval seat pads. */
export function Stadium({ size, ...rest }: Omit<SlabPartProps, 'radius'>) {
  return <Part geometry={stadiumSlabGeo(size[0], size[1], size[2])} {...rest} />
}

export function Ellipse({ size, ...rest }: Omit<SlabPartProps, 'radius'>) {
  return <Part geometry={ellipseSlabGeo(size[0], size[1], size[2])} {...rest} />
}

export interface CylPartProps extends Base {
  r: number
  h: number
  /** Top radius, for tapered forms (defaults to `r`). */
  rTop?: number
  segments?: number
  openEnded?: boolean
  /** Axis the cylinder runs along. Overrides `rotation` when set. */
  axis?: 'x' | 'y' | 'z'
}

const AXIS_ROTATION = {
  x: [0, 0, Math.PI / 2],
  y: [0, 0, 0],
  z: [Math.PI / 2, 0, 0],
} as const

export function Cyl({
  r,
  h,
  rTop,
  segments = 24,
  openEnded = false,
  axis,
  rotation,
  ...rest
}: CylPartProps) {
  return (
    <Part
      geometry={cylinderGeo(rTop ?? r, r, h, segments, openEnded)}
      rotation={axis ? AXIS_ROTATION[axis] : rotation}
      {...rest}
    />
  )
}

export function Sphere({ r, ...rest }: Base & { r: number }) {
  return <Part geometry={sphereGeo(r)} {...rest} />
}

const UP = new THREE.Vector3(0, 1, 0)

export interface BarProps extends Base {
  from: [number, number, number]
  to: [number, number, number]
  r?: number
  segments?: number
}

/**
 * A cylinder spanning two points. The workhorse for tubular frames: cantilever
 * chair sleds, table underframes, monitor arms, coat stands.
 */
export function Bar({ from, to, r = 0.015, segments = 12, ...rest }: BarProps) {
  // Endpoints usually arrive as fresh array literals, so memoise on their values.
  const endpoints = `${from.join()}|${to.join()}`
  const { position, quaternion, length } = useMemo(() => {
    const [ax, ay, az, bx, by, bz] = endpoints.replace('|', ',').split(',').map(Number)
    const a = new THREE.Vector3(ax, ay, az)
    const b = new THREE.Vector3(bx, by, bz)
    const delta = b.clone().sub(a)
    return {
      position: a.clone().add(b).multiplyScalar(0.5),
      quaternion: new THREE.Quaternion().setFromUnitVectors(UP, delta.clone().normalize()),
      length: delta.length(),
    }
  }, [endpoints])

  if (length < 1e-5) return null
  return (
    <Part
      geometry={cylinderGeo(r, r, length, segments)}
      position={position}
      quaternion={quaternion}
      {...rest}
    />
  )
}

export function Capsule({ r, length, ...rest }: Base & { r: number; length: number }) {
  return <Part geometry={capsuleGeo(r, length)} {...rest} />
}

export function Ring({ r, tube, ...rest }: Base & { r: number; tube: number }) {
  return <Part geometry={torusGeo(r, tube)} {...rest} />
}

export interface PanelPartProps extends Base {
  /** `[width, height]` of the face; extruded along local Z. */
  size: [number, number]
  thickness?: number
}

/** Thin upright panel — screens, dividers, whiteboards, framed art. */
export function Panel({ size, thickness = 0.012, ...rest }: PanelPartProps) {
  return <Part geometry={panelGeo(size[0], size[1], thickness)} {...rest} />
}

export interface LathePartProps extends Base {
  /** `[radius, y]` pairs from bottom to top. */
  profile: readonly (readonly [number, number])[]
  segments?: number
  profileKey?: string
}

/** Revolved profile — pots, bins, vases, pedestal bases. */
export function Lathe({ profile, segments = 28, profileKey, ...rest }: LathePartProps) {
  return <Part geometry={latheGeo(profile, segments, profileKey)} {...rest} />
}

export function Leaf({
  length,
  width,
  ...rest
}: Base & { length: number; width: number }) {
  return <Part geometry={leafGeo(length, width)} {...rest} />
}
